import React, { useState, useEffect } from "react";
import { FaChevronDown, FaPhoneAlt, FaEnvelope, FaBars, FaTimes } from "react-icons/fa";
import { Link, withRouter } from "react-router-dom";
import "./Navbar.css";
import vardaanVLogo from "../assets/Home/Vardaan V icon.png";

const Navbar = ({ history, location }) => {
  const [showProductsServicesDropdown, setShowProductsServicesDropdown] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState("home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Update currentPage based on location (strip basename /vardaanglobal so /vardaanglobal/blog -> blog)
  useEffect(() => {
    let path = location.pathname.replace(/^\/vardaanglobal\/?/i, '').replace(/^\//, '');
    setCurrentPage(path || "home");
  }, [location]);

  // Handle mobile screen detection and menu closing
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Initialize mobile state on component mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    closeDropdowns();
  }, [location.pathname]);

  // Handle body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
      
      // Force navbar links visibility
      const navbarLinks = document.querySelector('.navbar-links');
      if (navbarLinks) {
        navbarLinks.style.display = 'flex';
        navbarLinks.style.opacity = '1';
        navbarLinks.style.visibility = 'visible';
      }
    } else {
      document.body.classList.remove('mobile-menu-open');
      
      // Hide navbar links
      const navbarLinks = document.querySelector('.navbar-links');
      if (navbarLinks && window.innerWidth <= 768) {
        navbarLinks.style.display = 'none';
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);

  const navigateTo = (route) => {
    setCurrentPage(route);
    
    // Handle navigation based on route
    switch(route) {
      case "home":
        history.push("/");
        break;
      case "about":
        history.push("/about");
        break;
      case "contact":
        history.push("/contact");
        break;
      case "resources":
        history.push("/resources");
        break;
      case "blog":
        history.push("/blog");
        break;
      case "newsroom":
        history.push("/newsroom");
        break;
      default:
        history.push(`/${route}`);
        break;
    }
  };

  const toggleDropdown = (dropdownType) => {
    if (dropdownType === "productsServices") {
      setShowAboutDropdown(false);
      setShowProductsServicesDropdown(prev => !prev);
    } else if (dropdownType === "about") {
      setShowProductsServicesDropdown(false);
      setShowAboutDropdown(prev => !prev);
    }
  };

  const handleDropdownHover = (dropdownType, isHovering) => {
    if (isMobile) return;

    if (dropdownType === "productsServices") {
      setShowProductsServicesDropdown(isHovering);
      if (isHovering) {
        setShowAboutDropdown(false);
      }
    } else if (dropdownType === "about") {
      setShowAboutDropdown(isHovering);
      if (isHovering) {
        setShowProductsServicesDropdown(false);
      }
    }
  };

  const closeDropdowns = () => {
    setShowProductsServicesDropdown(false);
    setShowAboutDropdown(false);
  };

  const toggleMobileMenu = () => {
    const newMenuState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newMenuState);
    
    // Close dropdowns when opening menu
    if (newMenuState) {
      closeDropdowns();
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    
    // Force navbar links visibility
    const navbarLinks = document.querySelector('.navbar-links');
    if (navbarLinks) {
      if (newMenuState) {
        navbarLinks.style.display = 'flex';
      } else {
        navbarLinks.style.display = 'none';
      }
    }
  };

  const handleMobileNavClick = (route) => {
    navigateTo(route);
    setIsMobileMenuOpen(false);
    closeDropdowns();
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const aboutDropdownItems = [
    { name: "Company", route: "about" },
    { name: "Partners", route: "consortium-partners" },
    { name: "News & Events", route: "news-events" }
  ];

  const productsDropdownItems = [
    { name: "Products", route: "products" },
    { name: "Services", route: "services" },
    { name: "Product Resources", route: "resources" }
  ];

  const isAboutDropdownActive = aboutDropdownItems.some((item) => currentPage === item.route);
  const isProductsDropdownActive = productsDropdownItems.some((item) => currentPage === item.route);
  const isNewsroomActive = currentPage === "newsroom" || currentPage.startsWith("newsroom/");

  return (
    <nav className="navbar" style={{ backgroundColor: "#fff", position: "relative", zIndex: 10000 }}>
      {/* Logo Section */}
      <div className="navbar-left" style={{ backgroundColor: "#fff" }}>
        <Link to="/" onClick={() => navigateTo("home")} className="navbar-brand-link" style={{ backgroundColor: "#fff" }}>
          <img src={vardaanVLogo} alt="Vardaan Global Logo" className="logo-img" style={{ backgroundColor: "#fff" }} />
          <span className="navbar-brand-text">Vardaan Global</span>
        </Link>
      </div>

      {/* Mobile Menu Toggle */}
      <button 
        className={`mobile-menu-toggle ${isMobile ? 'mobile-visible' : ''}`}
        onClick={toggleMobileMenu}
        aria-label="Toggle mobile menu"
        style={{ backgroundColor: "#fff" }}
      >
        {isMobileMenuOpen ? 
          <FaTimes style={{ backgroundColor: "#fff" }} /> : 
          <FaBars style={{ backgroundColor: "#fff" }} />
        }
      </button>

      {/* Navigation Links - Desktop */}
      {!isMobile && (
        <ul className="navbar-links">
          <li>
            <a
              href="#"
              className={currentPage === "home" ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                navigateTo("home");
                closeDropdowns();
              }}
            >
              Home
            </a>
          </li>
          <li
            className={`dropdown-container ${isAboutDropdownActive ? "active" : ""}`}
            onMouseEnter={() => handleDropdownHover("about", true)}
            onMouseLeave={() => handleDropdownHover("about", false)}
          >
            <a
              href="#about"
              onClick={(e) => {
                e.preventDefault();
                if (isMobile) {
                  toggleDropdown("about");
                }
              }}
              className={(showAboutDropdown || isAboutDropdownActive) ? "active" : ""}
            >
              About Vardaan <FaChevronDown className="dropdown-icon" />
            </a>
            {showAboutDropdown && (
              <div
                className="dropdown-menu"
                onMouseEnter={() => handleDropdownHover("about", true)}
                onMouseLeave={() => handleDropdownHover("about", false)}
              >
                <div className="dropdown-column">
                  {aboutDropdownItems.map((item) => (
                    <a
                      key={item.route}
                      href="#"
                      className={currentPage === item.route ? "active" : ""}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateTo(item.route);
                        closeDropdowns();
                      }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </li>
          <li
            className={`dropdown-container ${isProductsDropdownActive ? "active" : ""}`}
            onMouseEnter={() => handleDropdownHover("productsServices", true)}
            onMouseLeave={() => handleDropdownHover("productsServices", false)}
          >
            <a
              href="#products-services"
              onClick={(e) => {
                e.preventDefault();
                if (isMobile) {
                  toggleDropdown("productsServices");
                }
              }}
              className={(showProductsServicesDropdown || isProductsDropdownActive) ? "active" : ""}
            >
              Our Products and Services <FaChevronDown className="dropdown-icon" />
            </a>
            {showProductsServicesDropdown && (
              <div
                className="dropdown-menu"
                onMouseEnter={() => handleDropdownHover("productsServices", true)}
                onMouseLeave={() => handleDropdownHover("productsServices", false)}
              >
                <div className="dropdown-column">
                  {productsDropdownItems.map((item) => (
                    <a
                      key={item.route}
                      href="#"
                      className={currentPage === item.route ? "active" : ""}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateTo(item.route);
                        closeDropdowns();
                      }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </li>
          <li>
            <a
              href="#blog"
              className={currentPage === "blog" ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                navigateTo("blog");
                closeDropdowns();
              }}
            >
              Blogs
            </a>
          </li>
          <li>
            <a
              href="#newsroom"
              className={isNewsroomActive ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                navigateTo("newsroom");
                closeDropdowns();
              }}
            >
              Newsroom
            </a>
          </li>
          <li>
            <a
              href="#contact"
              className={currentPage === "contact" ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                navigateTo("contact");
                closeDropdowns();
              }}
            >
              Contact Us
            </a>
          </li>
        </ul>
      )}

      {/* Navigation Links - Mobile */}
      {isMobile && isMobileMenuOpen && (
        <div 
          style={{
            position: 'fixed',
            top: '70px',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#fff',
            zIndex: 9990,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
          }}
        >
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            <li style={{ borderBottom: '1px solid #f0f0f0', width: '100%' }}>
              <a
                href="#"
                style={{ 
                  display: 'block', 
                  padding: '15px', 
                  color: currentPage === "home" ? "#1565c0" : "#333",
                  textDecoration: 'none'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleMobileNavClick("home");
                }}
              >
                Home
              </a>
            </li>
            <li style={{ borderBottom: '1px solid #f0f0f0', width: '100%' }}>
              <a
                href="#"
                style={{ 
                  display: 'block', 
                  padding: '15px', 
                  color: isAboutDropdownActive ? "#1565c0" : "#333",
                  textDecoration: 'none'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  toggleDropdown("about");
                }}
              >
                About Vardaan <FaChevronDown style={{ marginLeft: '5px', transform: showAboutDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
              </a>
              {showAboutDropdown && (
                <div style={{ padding: '0 0 10px 20px', background: '#fff' }}>
                  {aboutDropdownItems.map((item) => (
                    <a
                      key={item.route}
                      href="#"
                      style={{ 
                        display: 'block', 
                        padding: '10px 15px', 
                        color: currentPage === item.route ? "#1565c0" : "#333",
                        textDecoration: 'none',
                        borderLeft: currentPage === item.route ? "3px solid #1565c0" : "none",
                        backgroundColor: currentPage === item.route ? "#f3f8ff" : "transparent",
                        paddingLeft: currentPage === item.route ? "25px" : "15px"
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleMobileNavClick(item.route);
                      }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              )}
            </li>
            <li style={{ borderBottom: '1px solid #f0f0f0', width: '100%' }}>
              <a
                href="#"
                style={{ 
                  display: 'block', 
                  padding: '15px', 
                  color: isProductsDropdownActive ? "#1565c0" : "#333",
                  textDecoration: 'none'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  toggleDropdown("productsServices");
                }}
              >
                Our Products and Services <FaChevronDown style={{ marginLeft: '5px', transform: showProductsServicesDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
              </a>
              {showProductsServicesDropdown && (
                <div style={{ padding: '0 0 10px 20px', background: '#fff' }}>
                  {productsDropdownItems.map((item) => (
                    <a
                      key={item.route}
                      href="#"
                      style={{ 
                        display: 'block', 
                        padding: '10px 15px', 
                        color: currentPage === item.route ? "#1565c0" : "#333",
                        textDecoration: 'none',
                        borderLeft: currentPage === item.route ? "3px solid #1565c0" : "none",
                        backgroundColor: currentPage === item.route ? "#f3f8ff" : "transparent",
                        paddingLeft: currentPage === item.route ? "25px" : "15px"
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleMobileNavClick(item.route);
                      }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              )}
            </li>
            <li style={{ borderBottom: '1px solid #f0f0f0', width: '100%' }}>
              <a
                href="#"
                style={{ 
                  display: 'block', 
                  padding: '15px', 
                  color: currentPage === "blog" ? "#1565c0" : "#333",
                  textDecoration: 'none'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleMobileNavClick("blog");
                }}
              >
                Blogs
              </a>
            </li>
            <li style={{ borderBottom: '1px solid #f0f0f0', width: '100%' }}>
              <a
                href="#"
                style={{ 
                  display: 'block', 
                  padding: '15px', 
                  color: isNewsroomActive ? "#1565c0" : "#333",
                  textDecoration: 'none'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleMobileNavClick("newsroom");
                }}
              >
                Newsroom
              </a>
            </li>
            <li style={{ width: '100%' }}>
              <a
                href="#"
                style={{ 
                  display: 'block', 
                  padding: '15px', 
                  color: currentPage === "contact" ? "#1565c0" : "#333",
                  textDecoration: 'none'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleMobileNavClick("contact");
                }}
              >
                Contact Us
              </a>
            </li>
          </ul>
          
          {/* Mobile Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            padding: '15px',
            marginTop: 'auto',
            borderTop: '1px solid #f0f0f0'
          }}>
            <a 
              href="tel:+1234567890" 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                color: '#000',
                padding: '12px',
                background: '#fff',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                width: '45%',
                justifyContent: 'center'
              }}
            >
              <FaPhoneAlt style={{ color: '#1f70c6' }} />
              <span>Call Us</span>
            </a>
            <a 
              href="mailto:info@vardaan.com" 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none',
                color: '#000',
                padding: '12px',
                background: '#fff',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                width: '45%',
                justifyContent: 'center'
              }}
            >
              <FaEnvelope style={{ color: '#1f70c6' }} />
              <span>Email Us</span>
            </a>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="mobile-overlay" onClick={toggleMobileMenu}></div>}
    </nav>
  );
};

export default withRouter(Navbar);